import type { ActionRecord, ColumnKey, SectionRecord } from "../types.ts"
import type { PermissionFeatures } from "../types/permissions.types.ts"
import {
  type NestedValues,
  getCascadedOffFields,
  getCascadedOnFields
} from "./dependencies.ts"
import { getColKey, getFullAccessKey, syncFullAccess } from "./utils.ts"

export type FieldEntry = {
  name: [string, string, string]
  value: boolean
  touched: boolean
}

const isCrudColKey = (key: string, rowActions: ActionRecord): boolean => {
  const col = getColKey(key, rowActions)
  return (
    col === "create" || col === "view" || col === "delete" || col === "edit"
  )
}

const getEffectiveValue = (
  p: string,
  s: string,
  key: string,
  allValues: NestedValues,
  fieldMap: Map<string, FieldEntry>
): boolean | undefined => {
  const pending = fieldMap.get([p, s, key].join("."))
  if (pending) return pending.value
  return allValues[p]?.[s]?.[key]
}

export function applyOffCascade(
  disabledKey: string,
  allValues: NestedValues,
  fieldMap: Map<string, FieldEntry>
): void {
  getCascadedOffFields(disabledKey, allValues).forEach(([p, s, k]) => {
    const mapKey = [p, s, k].join(".")
    if (!fieldMap.has(mapKey)) {
      fieldMap.set(mapKey, { name: [p, s, k], value: false, touched: true })
    }

    // sync row's full_access OFF when any CRUD key gets cascaded off
    const rowActions = allValues[p]?.[s]
    if (!rowActions || !isCrudColKey(k, rowActions)) return
    const fullAccessKey = getFullAccessKey(rowActions)
    if (fullAccessKey && rowActions[fullAccessKey] === true) {
      const faMapKey = [p, s, fullAccessKey].join(".")
      if (!fieldMap.has(faMapKey)) {
        fieldMap.set(faMapKey, {
          name: [p, s, fullAccessKey],
          value: false,
          touched: true
        })
      }
    }
  })
}

export function applyOnCascade(
  enabledKey: string,
  allValues: NestedValues,
  fieldMap: Map<string, FieldEntry>
): void {
  getCascadedOnFields(enabledKey, allValues).forEach(([p, s, k]) => {
    const mapKey = [p, s, k].join(".")
    if (!fieldMap.has(mapKey)) {
      fieldMap.set(mapKey, { name: [p, s, k], value: true, touched: true })
    }

    // sync row's full_access ON when all CRUD keys become enabled
    const rowActions = allValues[p]?.[s]
    if (!rowActions || !isCrudColKey(k, rowActions)) return
    const fullAccessKey = getFullAccessKey(rowActions)
    if (!fullAccessKey || rowActions[fullAccessKey] === true) return
    const crudKeys = Object.keys(rowActions).filter((key) =>
      isCrudColKey(key, rowActions)
    )
    const allCrudOn = crudKeys.every(
      (crudKey) =>
        getEffectiveValue(p, s, crudKey, allValues, fieldMap) === true
    )
    if (!allCrudOn) return
    const faMapKey = [p, s, fullAccessKey].join(".")
    if (!fieldMap.has(faMapKey)) {
      fieldMap.set(faMapKey, {
        name: [p, s, fullAccessKey],
        value: true,
        touched: true
      })
    }
  })
}

export function getBulkColumnChangeFields(
  parentKey: string,
  colKey: ColumnKey,
  subKeys: SectionRecord,
  value: boolean,
  allValues: NestedValues
): FieldEntry[] {
  const fieldMap = new Map<string, FieldEntry>()

  Object.entries(subKeys).forEach(([subKey, actions]) => {
    const actionKey = Object.keys(actions).find(
      (action) => getColKey(action, actions) === colKey
    )
    const currentActions =
      (allValues[parentKey]?.[subKey] as ActionRecord) ?? actions

    if (!actionKey) {
      // Rows without a full_access code of their own (Company Settings and
      // Roles & Permissions expose only view + edit) still belong to the
      // Full Access column, so drive their CRUD keys directly.
      if (colKey !== "full_access") return

      Object.keys(actions)
        .filter((k) => isCrudColKey(k, actions))
        .forEach((k) => {
          if (currentActions[k] === value) return
          fieldMap.set([parentKey, subKey, k].join("."), {
            name: [parentKey, subKey, k],
            value,
            touched: true
          })
          if (value === false) applyOffCascade(k, allValues, fieldMap)
          if (value === true) applyOnCascade(k, allValues, fieldMap)
        })
      return
    }

    const synced = syncFullAccess(currentActions, actionKey, value)

    Object.entries(synced).forEach(([k, v]) => {
      if (v === currentActions[k]) return
      fieldMap.set([parentKey, subKey, k].join("."), {
        name: [parentKey, subKey, k],
        value: v as boolean,
        touched: true
      })
      if (v === false) applyOffCascade(k, allValues, fieldMap)
      if (v === true) applyOnCascade(k, allValues, fieldMap)
    })
  })

  return [...fieldMap.values()]
}

export function getValuesChangeFields(
  changedValues: Partial<PermissionFeatures>,
  allValues: NestedValues
): FieldEntry[] {
  const fieldMap = new Map<string, FieldEntry>()

  Object.entries(changedValues).forEach(([parentKey, subChanges]) => {
    if (!subChanges) return
    Object.entries(
      subChanges as Record<string, Record<string, boolean>>
    ).forEach(([subKey, actionChanges]) => {
      if (!actionChanges) return
      Object.entries(actionChanges).forEach(([actionKey, newValue]) => {
        const currentActions = allValues[parentKey]?.[subKey] as ActionRecord
        if (!currentActions) return

        const synced = syncFullAccess(currentActions, actionKey, newValue)

        Object.entries(synced).forEach(([k, v]) => {
          if (k === actionKey) return
          if (v === currentActions[k]) return
          fieldMap.set([parentKey, subKey, k].join("."), {
            name: [parentKey, subKey, k],
            value: v as boolean,
            touched: true
          })
          if (v === false) applyOffCascade(k, allValues, fieldMap)
          if (v === true) applyOnCascade(k, allValues, fieldMap)
        })

        if (newValue === false) applyOffCascade(actionKey, allValues, fieldMap)
        if (newValue === true) applyOnCascade(actionKey, allValues, fieldMap)
      })
    })
  })

  return [...fieldMap.values()]
}

export function getBulkFeaturesChangeFields(
  parentKey: string,
  features: ActionRecord,
  value: boolean,
  allValues: NestedValues,
  subKey = "features"
): FieldEntry[] {
  const fieldMap = new Map<string, FieldEntry>()

  Object.keys(features).forEach((featureKey) => {
    const currentFeatures = allValues[parentKey]?.[subKey] as
      | ActionRecord
      | undefined
    const currentVal = currentFeatures?.[featureKey] ?? features[featureKey]
    if (currentVal === value) return

    fieldMap.set([parentKey, subKey, featureKey].join("."), {
      name: [parentKey, subKey, featureKey],
      value,
      touched: true
    })

    if (value === false) applyOffCascade(featureKey, allValues, fieldMap)
    if (value === true) applyOnCascade(featureKey, allValues, fieldMap)
  })

  return [...fieldMap.values()]
}

export function applyFieldsToPermissions<T extends NestedValues>(
  permissions: T,
  fields: FieldEntry[]
): T {
  if (fields.length === 0) return permissions

  const updated: NestedValues = { ...permissions }
  fields.forEach(({ name: [p, s, k], value }) => {
    const parent = updated[p] ?? {}
    const section = parent[s] ?? {}
    updated[p] = {
      ...parent,
      [s]: { ...section, [k]: value }
    }
  })

  return updated as T
}
